// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import {
  Building2, Wifi, WifiOff, AlertTriangle, ShieldAlert,
  Clock, ArrowUpCircle, Loader2, ChevronRight, Users, Network,
  ClipboardList, Gavel, MessageSquare
} from 'lucide-react'

interface Sev { total: number; critical: number; high: number; medium: number; low: number; has_breakdown: boolean }
interface Poam { total: number; open: number; overdue: number; completed: number; on_time_pct: number | null; open_critical: number; open_high: number; present: boolean }
interface Bod { buckets: Record<string, number>; labels: Record<string, string>; overdue_sla: number; open_forensic_cases: number; total_classified: number; present: boolean }
interface Lic {
  license_type: string; is_active: boolean; is_revoked: boolean; is_expired: boolean
  in_grace_period: boolean; expires_at: string | null; days_until_expiry: number | null
  expiring_soon: boolean; license_key_masked: string | null
}
interface CustomerCard {
  tenant_id: string; name: string; license: Lic | null
  installations: { total: number; online: number; offline: number; out_of_date: number }
  vulnerabilities: Sev; poam: Poam; bod: Bod; usage: { users: number; endpoints: number }
  last_seen: string | null; alerts: string[]; alert_count: number
}
interface Overview {
  customers: number
  installations: { total: number; online: number; offline: number; out_of_date: number }
  licenses_expiring_or_expired: number
  customers_with_alerts: number
  vulnerabilities: { critical: number; high: number; medium: number; low: number; total: number }
  poam: { open: number; overdue: number }
  bod: { overdue_sla: number; open_forensic_cases: number }
  open_tickets: number
  latest_version: string | null
}

const ALERT_LABEL: Record<string, string> = {
  license_revoked: 'License revoked',
  license_expired: 'License expired',
  license_expiring: 'License expiring',
  no_license: 'No license',
  all_offline: 'All offline',
  some_offline: 'Some offline',
  out_of_date: 'Update available',
  poam_overdue: 'POA&M overdue',
  bod_overdue: 'BOD SLA overdue',
}

function SeverityBar({ sev, compact = false }: { sev: Sev; compact?: boolean }) {
  if (!sev.has_breakdown && sev.total === 0) {
    return <span className="text-xs text-gray-500">No data</span>
  }
  const parts = [
    { n: sev.critical, c: 'bg-red-500', label: 'Critical' },
    { n: sev.high, c: 'bg-orange-500', label: 'High' },
    { n: sev.medium, c: 'bg-yellow-500', label: 'Medium' },
    { n: sev.low, c: 'bg-sky-600', label: 'Low' },
  ]
  const total = parts.reduce((a, p) => a + p.n, 0) || 1
  return (
    <div className={compact ? '' : 'space-y-1'}>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-800">
        {parts.map((p) => p.n > 0 && (
          <div key={p.label} className={p.c} style={{ width: `${(p.n / total) * 100}%` }} title={`${p.label}: ${p.n.toLocaleString()}`} />
        ))}
      </div>
      {!compact && sev.has_breakdown && (
        <div className="flex gap-3 text-xs text-gray-400">
          <span className="text-red-400">{sev.critical.toLocaleString()} crit</span>
          <span className="text-orange-400">{sev.high.toLocaleString()} high</span>
          <span className="text-yellow-400">{sev.medium.toLocaleString()} med</span>
          <span className="text-sky-400">{sev.low.toLocaleString()} low</span>
        </div>
      )}
    </div>
  )
}

function LicenseChip({ lic }: { lic: Lic | null }) {
  if (!lic) return <span className="rounded-full bg-gray-700 px-2 py-0.5 text-xs text-gray-300">No license</span>
  let cls = 'bg-green-500/15 text-green-400', text = lic.license_type
  if (lic.is_revoked) { cls = 'bg-red-500/15 text-red-400'; text = 'Revoked' }
  else if (lic.is_expired) { cls = 'bg-red-500/15 text-red-400'; text = 'Expired' }
  else if (lic.expiring_soon) { cls = 'bg-amber-500/15 text-amber-400'; text = `${lic.license_type} · ${lic.days_until_expiry}d` }
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${cls}`}>{text}</span>
}

function StatCard({ icon: Icon, label, value, sub, tone = 'default' }: any) {
  const tones: Record<string, string> = {
    default: 'text-primary-500', warn: 'text-amber-400', danger: 'text-red-400', ok: 'text-green-400',
  }
  return (
    <div className="rounded-lg border border-dark-border bg-dark-card p-4">
      <div className="flex items-center gap-2 text-gray-400">
        <Icon className={`h-4 w-4 ${tones[tone]}`} />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500">{sub}</div>}
    </div>
  )
}

export default function MSPDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [overview, setOverview] = useState<Overview | null>(null)
  const [customers, setCustomers] = useState<CustomerCard[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { load() }, [])

  const load = async () => {
    try {
      const [ov, cs] = await Promise.all([
        api.get('/api/portal/msp/overview'),
        api.get('/api/portal/msp/customers'),
      ])
      setOverview(ov.data)
      setCustomers(cs.data.customers)
    } catch (e) {
      console.error('Failed to load MSP fleet:', e)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary-500" /></div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Building2 className="h-6 w-6 text-primary-500" />
          Customer Fleet
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          {user?.msp_account_name || 'MSP'} · a read-only overview of every customer's ETS instance.
          For detail, sign in to a customer's ETS as an admin.
        </p>
      </div>

      {/* Fleet summary */}
      {overview && (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
            <StatCard icon={Building2} label="Customers" value={overview.customers} />
            <StatCard icon={Wifi} label="Online" tone="ok"
              value={`${overview.installations.online}/${overview.installations.total}`} sub="installations" />
            <StatCard icon={ArrowUpCircle} label="Out of date" tone={overview.installations.out_of_date ? 'warn' : 'default'}
              value={overview.installations.out_of_date} sub={overview.latest_version ? `latest ${overview.latest_version}` : ''} />
            <StatCard icon={Clock} label="Lic. expiring" tone={overview.licenses_expiring_or_expired ? 'warn' : 'default'}
              value={overview.licenses_expiring_or_expired} sub="≤30 days / expired" />
            <StatCard icon={AlertTriangle} label="Need attention" tone={overview.customers_with_alerts ? 'danger' : 'ok'}
              value={overview.customers_with_alerts} sub="customers with alerts" />
            <StatCard icon={ClipboardList} label="POA&M overdue" tone={overview.poam.overdue ? 'danger' : 'default'}
              value={overview.poam.overdue} sub={`${overview.poam.open} open`} />
            <StatCard icon={Gavel} label="Forensic cases" tone={overview.bod.open_forensic_cases ? 'warn' : 'default'}
              value={overview.bod.open_forensic_cases} sub={overview.bod.overdue_sla ? `${overview.bod.overdue_sla} SLA overdue` : 'BOD 26-04'} />
            <StatCard icon={MessageSquare} label="Open tickets" tone={overview.open_tickets ? 'warn' : 'default'}
              value={overview.open_tickets} sub="customer support" />
          </div>

          {/* Fleet-wide vulnerabilities */}
          <div className="rounded-lg border border-dark-border bg-dark-card p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <ShieldAlert className="h-4 w-4 text-primary-500" /> Fleet vulnerabilities
              </span>
              <span className="text-sm text-gray-400">{overview.vulnerabilities.total.toLocaleString()} total</span>
            </div>
            <SeverityBar sev={{ ...overview.vulnerabilities, has_breakdown: true }} />
          </div>
        </>
      )}

      {/* Customers */}
      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-400">
          Customers ({customers.length})
        </h2>
        {customers.length === 0 ? (
          <div className="rounded-lg border border-dark-border bg-dark-card p-8 text-center text-gray-400">
            No customers are assigned to this MSP account yet.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {customers.map((c) => (
              <button
                key={c.tenant_id}
                onClick={() => navigate(`/msp/customers/${c.tenant_id}`)}
                className="group rounded-lg border border-dark-border bg-dark-card p-4 text-left transition-colors hover:border-primary-500/50"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{c.name}</div>
                    <div className="mt-1"><LicenseChip lic={c.license} /></div>
                  </div>
                  <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-600 group-hover:text-primary-500" />
                </div>

                <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    {c.installations.online > 0 ? <Wifi className="h-3.5 w-3.5 text-green-400" /> : <WifiOff className="h-3.5 w-3.5 text-gray-500" />}
                    {c.installations.online}/{c.installations.total} online
                  </span>
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {c.usage.users}</span>
                  <span className="flex items-center gap-1"><Network className="h-3.5 w-3.5" /> {c.usage.endpoints}</span>
                  {c.installations.out_of_date > 0 && (
                    <span className="flex items-center gap-1 text-amber-400"><ArrowUpCircle className="h-3.5 w-3.5" /> update</span>
                  )}
                </div>

                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                    <span>Vulnerabilities</span>
                    <span>{c.vulnerabilities.total.toLocaleString()}</span>
                  </div>
                  <SeverityBar sev={c.vulnerabilities} compact />
                </div>

                {(c.poam.present || c.bod.present) && (
                  <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                    {c.poam.present && (
                      <span className="flex items-center gap-1">
                        <ClipboardList className="h-3.5 w-3.5" /> POA&amp;M {c.poam.open} open
                        {c.poam.overdue > 0 && <span className="text-red-400"> · {c.poam.overdue} overdue</span>}
                      </span>
                    )}
                    {c.bod.present && (
                      <span className="flex items-center gap-1">
                        <Gavel className="h-3.5 w-3.5" /> BOD {c.bod.open_forensic_cases} case{c.bod.open_forensic_cases !== 1 ? 's' : ''}
                        {c.bod.overdue_sla > 0 && <span className="text-red-400"> · {c.bod.overdue_sla} SLA</span>}
                      </span>
                    )}
                  </div>
                )}

                {c.alerts.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {c.alerts.map((a) => (
                      <span key={a} className="rounded bg-red-500/10 px-1.5 py-0.5 text-[11px] text-red-400">
                        {ALERT_LABEL[a] || a}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
